import React, { useState, useCallback, useMemo, useRef } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler, zoomPlugin);

const GG_SCRIPT = `(async () => {
  const islandId =
    document.querySelector('[data-id]')?.dataset.id || 240079;

  const url = \`/player-count-graph?range=1h&id=\${islandId}\`;

  const res = await fetch(url, {
    credentials: "omit"
  });

  const json = await res.json();

  // Convert to readable time series
  const { start, step, values } = json.data;

  const points = values.map((v, i) => ({
    timestamp: new Date((start + step * i) * 1000).toISOString(),
    players: v
  }));

  console.log("RAW RESPONSE:");
  console.log(json);

  console.log("PARSED TIME SERIES:");
  console.log(points);

  // Make it easy to copy
  window.__HOURLY_DATA__ = points;

  console.log(
    "\\u2714 Data stored as window.__HOURLY_DATA__ (copy from console)"
  );
})();`;

function formatTimestamp(ts) {
  const d = new Date(ts);
  const h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${m} ${ampm}`;
}

function computeStats(data) {
  if (!data || data.length === 0) return null;
  const values = data.map((d) => d.players);
  const peak = Math.max(...values);
  const low = Math.min(...values);
  const avg = Math.round(values.reduce((a, b) => a + b, 0) / values.length);
  const current = values[values.length - 1];
  const first = values[0];
  const change = current - first;
  const changePct = first !== 0 ? ((change / first) * 100).toFixed(1) : "0";
  const peakEntry = data[values.indexOf(peak)];
  const lowEntry = data[values.indexOf(low)];
  return { peak, low, avg, current, change, changePct, peakTime: peakEntry?.timestamp, lowTime: lowEntry?.timestamp };
}

function parseJsonInput(text) {
  const parsed = JSON.parse(text);
  if (!Array.isArray(parsed) || parsed.length === 0) return null;
  if (!parsed[0].timestamp || parsed[0].players === undefined) return null;
  return [...parsed].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
}

function mergeWithTrim(currentData, newData) {
  // Get the set of timestamps from the new data
  const newTimestamps = new Set(newData.map((d) => d.timestamp));

  // Find the last 10 entries in current data (closest to current time)
  const last10 = currentData.slice(-10);

  // Check which of those last 10 overlap with new data
  const overlappingTimestamps = new Set();
  for (const entry of last10) {
    if (newTimestamps.has(entry.timestamp)) {
      overlappingTimestamps.add(entry.timestamp);
    }
  }

  let trimmed;
  if (overlappingTimestamps.size > 0) {
    // Remove overlapping entries from the last 10 of current data
    // Keep everything before the last 10 as-is, then filter the last 10
    const beforeLast10 = currentData.slice(0, -10);
    const filteredLast10 = last10.filter((e) => !overlappingTimestamps.has(e.timestamp));
    trimmed = [...beforeLast10, ...filteredLast10];
  } else {
    // No overlap — just use current data as-is
    trimmed = currentData;
  }

  // Merge: deduplicate by timestamp, new data takes priority
  const map = new Map();
  for (const item of trimmed) map.set(item.timestamp, item);
  for (const item of newData) map.set(item.timestamp, item);

  return Array.from(map.values()).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
}

export function FortniteGGPage() {
  const [currentJson, setCurrentJson] = useState("");
  const [newJson, setNewJson] = useState("");
  const [parseError, setParseError] = useState(null);
  const [chartDataArr, setChartDataArr] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copiedScript, setCopiedScript] = useState(false);
  const chartRef = useRef(null);

  const handleLoadCurrent = useCallback(() => {
    setParseError(null);
    try {
      const sorted = parseJsonInput(currentJson);
      if (!sorted) {
        setParseError('Current data must be a non-empty array of { "timestamp", "players" } objects.');
        return;
      }
      setChartDataArr(sorted);
    } catch {
      setParseError("Invalid JSON in Current Data.");
    }
  }, [currentJson]);

  const handleMerge = useCallback(() => {
    setParseError(null);

    // Parse new data
    let newData;
    try {
      newData = parseJsonInput(newJson);
      if (!newData) {
        setParseError('New data must be a non-empty array of { "timestamp", "players" } objects.');
        return;
      }
    } catch {
      setParseError("Invalid JSON in New Data.");
      return;
    }

    // If no current chart data, also try to parse the current field
    let base = chartDataArr;
    if (!base) {
      try {
        base = parseJsonInput(currentJson);
      } catch {
        // ignore
      }
    }

    if (!base || base.length === 0) {
      setParseError("No current data to merge with. Paste current data and load it first, or load new data directly.");
      return;
    }

    const merged = mergeWithTrim(base, newData);
    setChartDataArr(merged);
  }, [currentJson, newJson, chartDataArr]);

  const handleCopyScript = useCallback(() => {
    navigator.clipboard.writeText(GG_SCRIPT).then(() => {
      setCopiedScript(true);
      setTimeout(() => setCopiedScript(false), 2000);
    });
  }, []);

  const handleCopy = useCallback(() => {
    if (!chartDataArr) return;
    navigator.clipboard.writeText(JSON.stringify(chartDataArr, null, 2)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [chartDataArr]);

  const handleDownload = useCallback(() => {
    if (!chartDataArr) return;
    const blob = new Blob([JSON.stringify(chartDataArr, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "fortnite-gg-players.json";
    a.click();
    URL.revokeObjectURL(url);
  }, [chartDataArr]);

  const handleClear = useCallback(() => {
    setChartDataArr(null);
    setCurrentJson("");
    setNewJson("");
    setParseError(null);
  }, []);

  const stats = useMemo(() => computeStats(chartDataArr), [chartDataArr]);

  const chartConfig = useMemo(() => {
    if (!chartDataArr) return null;

    const labels = chartDataArr.map((d) => formatTimestamp(d.timestamp));
    const values = chartDataArr.map((d) => d.players);

    const data = {
      labels,
      datasets: [
        {
          label: "Players",
          data: values,
          borderColor: "#FCE865",
          borderWidth: 2.5,
          backgroundColor: (ctx) => {
            const chart = ctx.chart;
            const { ctx: canvasCtx, chartArea } = chart;
            if (!chartArea) return "rgba(252, 232, 101, 0.1)";
            const gradient = canvasCtx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
            gradient.addColorStop(0, "rgba(252, 232, 101, 0.35)");
            gradient.addColorStop(0.5, "rgba(252, 232, 101, 0.08)");
            gradient.addColorStop(1, "rgba(252, 232, 101, 0)");
            return gradient;
          },
          fill: true,
          tension: 0.4,
          pointRadius: 0,
          pointHitRadius: 10,
          pointHoverRadius: 5,
          pointHoverBackgroundColor: "#FCE865",
          pointHoverBorderColor: "#fff",
          pointHoverBorderWidth: 2,
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "rgba(9, 9, 11, 0.95)",
          borderColor: "#FCE865",
          borderWidth: 1,
          titleColor: "#FCE865",
          bodyColor: "#e5e7eb",
          titleFont: { size: 13, weight: "bold" },
          bodyFont: { size: 14 },
          padding: 14,
          cornerRadius: 8,
          displayColors: false,
          callbacks: {
            title: (items) => items[0]?.label || "",
            label: (item) => `Players: ${item.raw.toLocaleString()}`,
          },
        },
        zoom: {
          pan: { enabled: true, mode: "x" },
          zoom: {
            wheel: { enabled: true },
            pinch: { enabled: true },
            mode: "x",
          },
        },
      },
      scales: {
        x: {
          ticks: {
            color: "#6b7280",
            font: { size: 11 },
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 15,
          },
          grid: { color: "rgba(39, 39, 42, 0.5)", drawBorder: false },
        },
        y: {
          ticks: {
            color: "#6b7280",
            font: { size: 11 },
            callback: (v) => v.toLocaleString(),
          },
          grid: { color: "rgba(39, 39, 42, 0.5)", drawBorder: false },
        },
      },
    };

    return { data, options };
  }, [chartDataArr]);

  return (
    <div>
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-200">Fortnite GG</h2>
          <p className="mt-2 text-sm text-gray-400">
            Paste player count data to visualize concurrent players over time.
          </p>
        </div>
        <button
          onClick={handleCopyScript}
          className="w-full sm:w-auto px-4 py-2 bg-fortpixel-gold hover:bg-yellow-600 text-black font-medium text-sm rounded-md transition-colors text-center"
        >
          {copiedScript ? "Copied!" : "Copy Javascript Fortnite GG Command"}
        </button>
      </div>

      {/* Paste Inputs */}
      <div className="bg-[#18181B] rounded-xl shadow-xl shadow-black/20 p-4 sm:p-6 mb-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-2">Current Data</h3>
            <textarea
              value={currentJson}
              onChange={(e) => setCurrentJson(e.target.value)}
              placeholder='[{ "timestamp": "...", "players": 485 }, ...]'
              rows={6}
              className="w-full px-3 py-2 bg-[#09090B] border border-gray-700 rounded-md text-gray-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-fortpixel-gold resize-y"
            />
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-2">New Data</h3>
            <textarea
              value={newJson}
              onChange={(e) => setNewJson(e.target.value)}
              placeholder='[{ "timestamp": "...", "players": 500 }, ...]'
              rows={6}
              className="w-full px-3 py-2 bg-[#09090B] border border-gray-700 rounded-md text-gray-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-fortpixel-gold resize-y"
            />
          </div>
        </div>
        {parseError && <p className="text-sm text-red-400 mt-3">{parseError}</p>}
        <div className="flex flex-wrap gap-3 mt-4">
          <button
            onClick={handleLoadCurrent}
            className="w-full sm:w-auto px-5 py-2 bg-fortpixel-gold hover:bg-yellow-600 text-black font-medium text-sm rounded-md transition-colors"
          >
            Load Current Data
          </button>
          <button
            onClick={handleMerge}
            className="w-full sm:w-auto px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium text-sm rounded-md transition-colors"
          >
            Merge
          </button>
          {chartDataArr && (
            <button
              onClick={handleClear}
              className="w-full sm:w-auto px-5 py-2 bg-[#27272A] hover:bg-[#3f3f46] text-gray-400 text-sm rounded-md transition-colors"
            >
              Clear
            </button>
          )}
        </div>
        <p className="text-xs text-gray-600 mt-2">
          "Load" graphs the current data. "Merge" trims the last 10 timestamps from current data where they overlap with new data, then combines both.
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 mb-6">
          {[
            { label: "Current", value: stats.current.toLocaleString(), color: "text-gray-200" },
            { label: "Peak", value: stats.peak.toLocaleString(), sub: stats.peakTime ? formatTimestamp(stats.peakTime) : "", color: "text-fortpixel-gold" },
            { label: "Low", value: stats.low.toLocaleString(), sub: stats.lowTime ? formatTimestamp(stats.lowTime) : "", color: "text-red-400" },
            { label: "Average", value: stats.avg.toLocaleString(), color: "text-blue-400" },
            { label: "Change", value: `${stats.change >= 0 ? "+" : ""}${stats.change.toLocaleString()}`, color: stats.change >= 0 ? "text-green-400" : "text-red-400" },
            { label: "Change %", value: `${stats.change >= 0 ? "+" : ""}${stats.changePct}%`, color: stats.change >= 0 ? "text-green-400" : "text-red-400" },
          ].map((s) => (
            <div key={s.label} className="bg-[#18181B] rounded-xl p-4 shadow-xl shadow-black/20">
              <p className="text-xs text-gray-500 uppercase tracking-wider">{s.label}</p>
              <p className={`text-xl font-bold mt-1 ${s.color}`}>{s.value}</p>
              {s.sub && <p className="text-xs text-gray-600 mt-0.5">{s.sub}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Chart */}
      {chartConfig && (
        <div className="bg-[#18181B] rounded-xl shadow-xl shadow-black/20 p-4 sm:p-6">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider">
              Player Count — {chartDataArr.length} data points
            </h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => chartRef.current?.resetZoom()}
                className="w-full sm:w-auto px-3 py-1.5 bg-[#27272A] hover:bg-[#3f3f46] text-gray-400 text-xs rounded-md transition-colors"
              >
                Reset Zoom
              </button>
              <button
                onClick={handleCopy}
                className="w-full sm:w-auto px-3 py-1.5 bg-[#27272A] hover:bg-[#3f3f46] text-gray-300 text-sm rounded-md transition-colors"
              >
                {copied ? "Copied!" : "Copy JSON"}
              </button>
              <button
                onClick={handleDownload}
                className="w-full sm:w-auto px-3 py-1.5 bg-[#27272A] hover:bg-[#3f3f46] text-gray-300 text-sm rounded-md transition-colors"
              >
                Download JSON
              </button>
            </div>
          </div>
          <div className="h-[320px] sm:h-[450px]">
            <Line ref={chartRef} data={chartConfig.data} options={chartConfig.options} />
          </div>
          <p className="text-xs text-gray-600 mt-3">Scroll to zoom, drag to pan.</p>
        </div>
      )}
    </div>
  );
}
